import React from 'react';
import { Badge } from '../../components/ui/badge';

interface UserSystem {
    id: string;
    name: string;
    email: string;
    department: string;
    systemsCount: number;
    estMonthlyCost: string;
    primarySystems: string[];
}

interface SystemByUserProps {
    systems: any[];
    loading: boolean;
}

const SystemByUser: React.FC<SystemByUserProps> = ({ systems, loading }) => {
    // Transform systems data to group by user
    const userMap = new Map<number, any>();

    systems.forEach(system => {
        if (!system.assignedUsers) return;

        const costPerLicense = system.totalLicenses > 0 
            ? (parseFloat(system.monthlyCost) / system.totalLicenses) 
            : 0;

        system.assignedUsers.forEach((assignment: any) => {
            const user = assignment.user;
            if (!user) return;

            if (!userMap.has(user.id)) {
                userMap.set(user.id, {
                    id: user.id,
                    name: user.name,
                    email: user.email,
                    department: user.department?.departmentName || 'Unassigned',
                    systemsCount: 0,
                    estMonthlyCost: 0,
                    primarySystems: []
                });
            }

            const userData = userMap.get(user.id);
            userData.systemsCount += 1;
            userData.estMonthlyCost += costPerLicense;
            userData.primarySystems.push(system.name);
        });
    });

    const userList = Array.from(userMap.values()).sort((a, b) => b.systemsCount - a.systemsCount);

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h4 className="font-bold text-gray-800">System Access by User</h4>
                    <p className="text-xs text-gray-500 mt-1">View which systems each user has access to and associated costs</p>
                </div>

                {loading ? (
                    <div className="p-20 flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                    </div>
                ) : userList.length === 0 ? (
                    <div className="p-20 text-center text-gray-500">
                        No user assignments found.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50/50 text-[11px] uppercase tracking-wider font-bold text-gray-400">
                                    <th className="px-6 py-4">User</th>
                                    <th className="px-6 py-4">Department</th>
                                    <th className="px-6 py-4 text-center">Systems Count</th>
                                    <th className="px-6 py-4">Est. Monthly Cost</th>
                                    <th className="px-6 py-4">Primary Systems</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {userList.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div>
                                                <p className="text-sm font-bold text-gray-800">{user.name}</p>
                                                <p className="text-[11px] text-blue-500 font-medium">{user.email}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm font-medium text-blue-600 hover:underline cursor-pointer">
                                                {user.department}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <Badge variant="outline" className="rounded-full bg-blue-50 text-blue-700 border-blue-200 font-bold text-xs px-3">
                                                {user.systemsCount}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm font-bold text-gray-700">${user.estMonthlyCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-2">
                                                {user.primarySystems.map((system: string, i: number) => (
                                                    <Badge
                                                        key={`${user.id}-${system}-${i}`}
                                                        variant="outline"
                                                        className="rounded-lg bg-gray-50 text-gray-600 border-gray-200 font-bold text-[10px]"
                                                    >
                                                        {system}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SystemByUser;
